// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { isSmsTokenDTO, SmsTokenDTO } from "../../../../../../../io/hyperify/core/auth/sms/types/SmsTokenDTO";
import { SmsAuthSessionService } from "../../../../../../../io/hyperify/frontend/services/SmsAuthSessionService";
import { MY_REQUEST_CODE_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_REQUEST_CODE_CODE_FIELD_PLACEHOLDER,
    T_MY_REQUEST_CODE_BUTTON_LABEL,
    T_MY_REQUEST_CODE_ERROR_MESSAGE
} from "../../../../../core/constants/translation";

import { useCallback, useState } from "react";
import { TextField } from "../../../../../../../io/hyperify/frontend/components/fields/text/TextField";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { SubmitButton } from "../../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { EmailAuthSessionService } from "../../../../../../../io/hyperify/frontend/services/EmailAuthSessionService";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { EmailTokenDTO } from "../../../../../../../io/hyperify/core/auth/email/types/EmailTokenDTO";
import { TranslationParams } from "../../../../../../../io/hyperify/core/types/TranslationParams";
import "./MyRequestCodeArticle.scss";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { StringUtils } from "../../../../../../../io/hyperify/core/StringUtils";

const LOG = LogService.createLogger('MyRequestCodeArticle');

export interface MyRequestCodeViewProps {
    readonly className          ?: string;
    readonly t                   : TranslationFunction;
    readonly token               : EmailTokenDTO | SmsTokenDTO;
    readonly disableRequestCode  : () => void;
    readonly translationParams ?: TranslationParams;
    readonly title       : string;
    readonly contentText : string;
    readonly codePlaceholder ?: string;
    readonly submitButtonLabel ?: string;
    readonly errorMessage ?: string;
}

export function MyRequestCodeArticle (props: MyRequestCodeViewProps) {
    const className = props?.className;
    const t = props?.t;
    const disableRequestCode = props?.disableRequestCode;
    const token = props?.token;
    const title = props?.title;
    const contentText = props?.contentText;
    const translationParams = props?.translationParams ?? {};
    const codePlaceholder = props?.codePlaceholder ?? t(T_MY_REQUEST_CODE_CODE_FIELD_PLACEHOLDER, translationParams);
    const submitButtonLabel = props?.submitButtonLabel ?? t(T_MY_REQUEST_CODE_BUTTON_LABEL, translationParams);
    const errorMessage = props?.errorMessage ?? t(T_MY_REQUEST_CODE_ERROR_MESSAGE, translationParams);
    const [ code, setCode ] = useState<string>('');
    const [ hasError, setError ] = useState<boolean>(false);

    const handleCodeChange = useCallback(
        (value: string | undefined) => {
            const newCodeString = StringUtils.removeSpaces(value || '');
            if (newCodeString !== code) {
                setCode(newCodeString);
            }
        },
        [
            setCode,
            code
        ]
    );

    const submitCode = useCallback(
        async (token: EmailTokenDTO | SmsTokenDTO, code: string) => {
            try {
                if (isSmsTokenDTO(token)) {
                    LOG.debug(`submitCode with sms: `, token, code);
                    await SmsAuthSessionService.verifySmsCode(token, code);
                } else {
                    LOG.debug(`submitCode with email: `, token, code);
                    await EmailAuthSessionService.verifyEmailCode(token, code);
                }
                if (hasError) {
                    setError(false);
                }
                disableRequestCode();
            } catch (err) {
                LOG.error(`submitCode: Error: `, err);
                setError(true);
            }
        },
        [
            hasError,
            setError,
            disableRequestCode
        ]
    );

    const submitCallback = useCallback(
        () => {
            LOG.debug(`submitCallback: `, token, code);
            submitCode(token, code).catch(err => {
                LOG.error(`submitCallback: `, err);
            });
        },
        [
            token,
            code,
            submitCode
        ]
    );

    return (
        <article
            className={
                MY_REQUEST_CODE_ARTICLE_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >
            <Form
                className={`${MY_REQUEST_CODE_ARTICLE_CLASS_NAME}-form`}
                submit={submitCallback}
            >
                <h3>{title}</h3>
                <p>{contentText}</p>
                <TextField
                    className={`${MY_REQUEST_CODE_ARTICLE_CLASS_NAME}-email-field`}
                    placeholder={codePlaceholder}
                    value={code}
                    change={handleCodeChange}
                />
                <SubmitButton className={`${MY_REQUEST_CODE_ARTICLE_CLASS_NAME}-button buy-button button`}
                >{submitButtonLabel}</SubmitButton>
            </Form>
            {hasError ? <strong className={`${MY_REQUEST_CODE_ARTICLE_CLASS_NAME}-error-message`}>{errorMessage}</strong> : null}
        </article>
    );

}
